(() => {
  if (customElements.get('cart-document-type')) return;

  /**
   * Limpia puntos, caracteres extraños y normaliza la K.
   */
  const cleanRut = (value) => {
    return String(value || '')
      .replace(/\./g, '')
      .replace(/[^0-9kK-]/g, '')
      .toUpperCase();
  };

  /**
   * Formatea el RUT como 11222333-1.
   */
  const formatRut = (value) => {
    const clean = cleanRut(value).replace(/-/g, '');

    if (clean.length < 2) {
      return clean;
    }

    return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
  };

  /**
   * Comprueba el dígito verificador chileno.
   */
  const validRut = (value) => {
    const clean = cleanRut(value).replace(/-/g, '');

    if (!/^\d{7,8}[0-9K]$/.test(clean)) {
      return false;
    }

    const body = clean.slice(0, -1);
    const suppliedDigit = clean.slice(-1);

    let sum = 0;
    let multiplier = 2;

    for (
      let index = body.length - 1;
      index >= 0;
      index -= 1
    ) {
      sum += Number(body[index]) * multiplier;

      multiplier =
        multiplier === 7
          ? 2
          : multiplier + 1;
    }

    const result = 11 - (sum % 11);

    const expectedDigit =
      result === 11
        ? '0'
        : result === 10
          ? 'K'
          : String(result);

    return suppliedDigit === expectedDigit;
  };

  class CartDocumentType extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';

      this.form = this.closest('form');

      this.rutSection = this.querySelector(
        '[data-rut-section]'
      );

      this.rutInput = this.querySelector(
        '[data-rut-input]'
      );

      this.rutFeedback = this.querySelector(
        '[data-rut-feedback]'
      );

      this.invoiceFields = this.querySelector(
        '[data-invoice-fields]'
      );

      this.registeredHelp = this.querySelector(
        '[data-registered-help]'
      );

      this.status = this.querySelector(
        '[data-document-status]'
      );

      this.documentValueInput = this.querySelector(
        '[data-document-value]'
      );

      this.addEventListener(
        'change',
        this.handleChange
      );

      this.addEventListener(
        'input',
        this.handleInput
      );

      if (this.form) {
        this.form.addEventListener(
          'submit',
          this.handleSubmit
        );
      }

      this.updateMode();
      this.initializeRut();
    }

    disconnectedCallback() {
      this.removeEventListener(
        'change',
        this.handleChange
      );

      this.removeEventListener(
        'input',
        this.handleInput
      );

      if (this.form) {
        this.form.removeEventListener(
          'submit',
          this.handleSubmit
        );
      }
    }

    /**
     * Devuelve el modo visual seleccionado:
     * boleta, factura o registrado.
     */
    mode() {
      const selected = this.querySelector(
        '[data-document-type]:checked'
      );

      return selected?.value || 'boleta';
    }

    /**
     * Cambio entre Boleta, Factura y Ya soy cliente.
     */
    handleChange = (event) => {
      if (
        !event.target.matches('[data-document-type]')
      ) {
        return;
      }

      this.updateMode();
    };

    /**
     * Validación mientras el cliente escribe.
     */
    handleInput = (event) => {
      const input = event.target;

      if (input.matches('[data-rut-input]')) {
        input.value = formatRut(input.value);

        this.updateRutFeedback();

        if (this.status) {
          this.status.textContent = '';
        }

        return;
      }

      if (input.matches('[data-invoice-input]')) {
        input.setCustomValidity('');
        input.removeAttribute('aria-invalid');

        input
          .closest('.cart-document__field')
          ?.classList.remove('is-invalid');

        if (this.status) {
          this.status.textContent = '';
        }
      }
    };

    /**
     * Intercepta el envío normal del carrito.
     */
    handleSubmit = (event) => {
      const submitter = event.submitter;

      /*
       * No interfiere con otros botones del formulario,
       * por ejemplo Actualizar carrito.
       */
      if (
        submitter &&
        submitter.name &&
        submitter.name !== 'checkout'
      ) {
        return;
      }

      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      /*
       * Limpia los atributos que no corresponden justo
       * antes de enviar el formulario.
       */
      this.clearUnusedValues();

      /*
       * Refuerza el valor que debe recibir Shopify y Obuma.
       */
      this.updateDocumentValue();
    };

    /**
     * Define el valor tributario enviado a Shopify y Obuma.
     *
     * Boleta:
     * shoppingcart-tags = boleta
     *
     * Factura:
     * shoppingcart-tags = factura
     *
     * Ya soy cliente:
     * shoppingcart-tags = factura
     */
    updateDocumentValue() {
      if (!this.documentValueInput) {
        return;
      }

      const currentMode = this.mode();

      this.documentValueInput.value =
        currentMode === 'boleta'
          ? 'boleta'
          : 'factura';
    }

    /**
     * Muestra las secciones correspondientes.
     */
    updateMode() {
      const currentMode = this.mode();

      this.updateDocumentValue();

      const requiresRut =
        currentMode === 'factura' ||
        currentMode === 'registrado';

      const requiresInvoice =
        currentMode === 'factura';

      if (this.rutSection) {
        this.rutSection.hidden = !requiresRut;
      }

      if (this.invoiceFields) {
        this.invoiceFields.hidden = !requiresInvoice;
      }

      if (this.registeredHelp) {
        this.registeredHelp.hidden =
          currentMode !== 'registrado';
      }

      if (this.rutInput) {
        this.rutInput.required = requiresRut;

        if (!requiresRut) {
          this.rutInput.setCustomValidity('');
        }
      }

      this
        .querySelectorAll('[data-invoice-input]')
        .forEach((input) => {
          input.required = requiresInvoice;

          if (!requiresInvoice) {
            input.setCustomValidity('');
          }
        });

      this.clearVisualErrors();

      /*
       * Si existe un RUT escrito, mantiene visible
       * su resultado de validación.
       */
      if (requiresRut && this.rutInput?.value) {
        this.updateRutFeedback();
      }
    }

    /**
     * Inicializa y valida un RUT recuperado por Liquid.
     */
    initializeRut() {
      if (
        !this.rutInput ||
        !this.rutInput.value
      ) {
        return;
      }

      this.rutInput.value = formatRut(
        this.rutInput.value
      );

      this.updateRutFeedback();
    }

    /**
     * Muestra RUT válido o RUT inválido.
     */
    updateRutFeedback() {
      if (!this.rutInput) {
        return false;
      }

      const field = this.rutInput.closest(
        '.cart-document__field'
      );

      const hasValue =
        this.rutInput.value.trim() !== '';

      const isValid =
        hasValue &&
        validRut(this.rutInput.value);

      field?.classList.remove(
        'is-valid',
        'is-invalid'
      );

      this.rutInput.removeAttribute(
        'aria-invalid'
      );

      if (!hasValue) {
        this.rutInput.setCustomValidity('');

        if (this.rutFeedback) {
          this.rutFeedback.textContent = '';
        }

        return false;
      }

      if (isValid) {
        this.rutInput.setCustomValidity('');

        field?.classList.add('is-valid');

        this.rutInput.setAttribute(
          'aria-invalid',
          'false'
        );

        if (this.rutFeedback) {
          this.rutFeedback.textContent =
            'RUT válido';
        }

        return true;
      }

      this.rutInput.setCustomValidity(
        'Ingresa un RUT chileno válido.'
      );

      field?.classList.add('is-invalid');

      this.rutInput.setAttribute(
        'aria-invalid',
        'true'
      );

      if (this.rutFeedback) {
        this.rutFeedback.textContent =
          'RUT inválido';
      }

      return false;
    }

    /**
     * Retira estados visuales anteriores.
     */
    clearVisualErrors() {
      this
        .querySelectorAll(
          '.cart-document__field.is-invalid'
        )
        .forEach((field) => {
          field.classList.remove('is-invalid');
        });

      this
        .querySelectorAll(
          '[data-invoice-input]'
        )
        .forEach((input) => {
          input.removeAttribute('aria-invalid');
          input.setCustomValidity('');
        });

      if (this.status) {
        this.status.textContent = '';
      }
    }

    /**
     * Limpia los datos que no corresponden al modo.
     *
     * Boleta:
     * limpia RUT y todos los datos de factura.
     *
     * Ya soy cliente:
     * conserva RUT y limpia los datos manuales.
     *
     * Factura:
     * conserva todos los datos.
     */
    clearUnusedValues() {
      const currentMode = this.mode();

      if (
        currentMode === 'boleta' &&
        this.rutInput
      ) {
        this.rutInput.value = '';
      }

      if (currentMode !== 'factura') {
        this
          .querySelectorAll(
            '[data-invoice-input]'
          )
          .forEach((input) => {
            input.value = '';
          });
      }
    }

    /**
     * Valida los datos necesarios antes de pagar.
     */
    validate() {
      const currentMode = this.mode();

      if (currentMode === 'boleta') {
        return true;
      }

      let firstInvalid = null;

      /*
       * Factura y Ya soy cliente requieren RUT.
       */
      if (!this.updateRutFeedback()) {
        firstInvalid = this.rutInput;
      }

      /*
       * Solo Factura requiere los demás campos.
       */
      if (currentMode === 'factura') {
        this
          .querySelectorAll(
            '[data-invoice-input]'
          )
          .forEach((input) => {
            const isValid =
              input.value.trim() !== '' &&
              input.checkValidity();

            const field = input.closest(
              '.cart-document__field'
            );

            field?.classList.toggle(
              'is-invalid',
              !isValid
            );

            input.setAttribute(
              'aria-invalid',
              String(!isValid)
            );

            if (!isValid && !firstInvalid) {
              firstInvalid = input;
            }
          });
      }

      if (!firstInvalid) {
        if (this.status) {
          this.status.textContent = '';
        }

        return true;
      }

      if (this.status) {
        this.status.textContent =
          currentMode === 'registrado'
            ? 'Ingresa un RUT válido para continuar.'
            : 'Revisa los datos de facturación antes de continuar.';
      }

      firstInvalid?.focus();
      firstInvalid?.reportValidity();

      this.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });

      return false;
    }
  }

  customElements.define(
    'cart-document-type',
    CartDocumentType
  );
})();